import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ImproveRow } from '../lib/api';
import { canScreen, useMe } from '../lib/me';
import { MANUAL, type ManualSection } from '../lib/manualGuide';
import { isNewSince, markManualSeen, readSeen } from '../lib/manualNews';

/**
 * 사용 설명서(2026-10-01 way — 「업데이트할 때마다 직원이 볼 수 있는 실시간 매뉴얼」, YDYOPS `/manual` 참조).
 *
 * 무엇이 «실시간»인가 — 사람이 따로 고치는 자리가 없다:
 *  - 본문 = `lib/manualGuide.json`(PPT 설명서와 같은 한 벌). 배포에 같이 실려 나가므로 **배포 = 설명서 갱신**.
 *  - 「최근 업데이트」 = 이 번들의 빌드 시각(`__APP_VERSION__`). 손으로 날짜를 적지 않는다(적으면 반드시 어긋난다).
 *  - 「최근 바뀐 것」 = 완료된 개선요청(전체공개만) — 게시판이 곧 변경 이력이다.
 * 기본은 **내가 열 수 있는 메뉴의 장만**(화면 권한 = 서버 계산값) — 「전체 보기」로 펼친다.
 */
const BUILD = typeof __APP_VERSION__ === 'string' ? Number(__APP_VERSION__) : NaN;
const CHANGE_DAYS = 30;

const kst = (ms: number) =>
  new Date(ms).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const day = (s: string) => s.slice(5, 10).replace('-', '.');

function Changes({ seen }: { seen: string | null }) {
  const [rows, setRows] = useState<ImproveRow[] | null>(null);
  useEffect(() => {
    let off = false;
    api.listImprovements({ status: 'done', limit: 200 })
      .then((r) => { if (!off) setRows(r.rows); })
      .catch(() => { if (!off) setRows([]); });
    return () => { off = true; };
  }, []);
  const recent = useMemo(() => {
    const since = new Date(Date.now() - CHANGE_DAYS * 86_400_000).toISOString().slice(0, 10);
    return (rows ?? [])
      .filter((r) => r.visibility === 'all')
      .map((r) => ({ r, at: r.last_reply_at ?? r.created_at }))
      .filter((x) => x.at.slice(0, 10) >= since)
      .sort((a, b) => b.at.localeCompare(a.at))
      .slice(0, 20);
  }, [rows]);

  return (
    <section id="changes" className="card scroll-mt-4 p-4">
      <h2 className="text-base font-bold">최근 바뀐 것</h2>
      <p className="mb-2 text-xs text-ink-soft">개선요청으로 들어와 반영된 것입니다(최근 {CHANGE_DAYS}일). 누르면 그 글이 열립니다.</p>
      {rows === null ? (
        <p className="text-sm text-ink-faint">불러오는 중…</p>
      ) : recent.length === 0 ? (
        <p className="text-sm text-ink-faint">최근 {CHANGE_DAYS}일 동안 반영된 요청이 없습니다.</p>
      ) : (
        <ul className="divide-y divide-line">
          {recent.map(({ r, at }) => (
            <li key={r.improvement_id}>
              <Link to={`/improve?open=${r.improvement_id}`} className="flex items-baseline gap-2 py-1.5 text-sm hover:bg-canvas">
                <span className="w-11 flex-shrink-0 tabular-nums text-xs text-ink-faint">{day(at)}</span>
                {r.menu && <span className="flex-shrink-0 rounded-full border border-line px-2 text-xs text-ink-soft">{r.menu}</span>}
                {isNewSince(r, seen) && <span className="flex-shrink-0 rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white">새</span>}
                <span className="min-w-0 truncate">{r.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Section({ s }: { s: ManualSection }) {
  const src = `/manual/${s.shot}.webp`;
  return (
    <article id={s.id} className="card scroll-mt-4 overflow-hidden">
      <header className="flex items-center gap-2 border-b border-line bg-canvas px-4 py-2">
        <h3 className="text-base font-bold">{s.title}</h3>
        <span className="ml-auto flex-shrink-0 rounded-full border border-line bg-surface px-2 text-xs text-ink-soft">{s.menu}</span>
      </header>
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <a href={src} target="_blank" rel="noreferrer" title="크게 보기" className="block self-start rounded-lg border border-line">
          <img src={src} alt={`${s.title} 화면`} loading="lazy" className="w-full rounded-lg" />
        </a>
        <ol className="space-y-2.5">
          {s.steps.map((t, i) => (
            <li key={i} className="flex gap-2.5 text-sm leading-relaxed">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">{i + 1}</span>
              <span className="break-keep">{t}</span>
            </li>
          ))}
        </ol>
      </div>
      {s.note && (
        <p className={`mx-4 mb-4 rounded-lg px-3 py-2 text-sm font-semibold ${s.note.tone === 'red' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
          {s.note.text}
        </p>
      )}
    </article>
  );
}

export default function Manual() {
  const { me } = useMe();
  const [all, setAll] = useState(false);
  const [term, setTerm] = useState('');
  // 열 때 «본 시각»을 옮긴다 — 직전 기준은 「새」 표시에 쓴다(메뉴 배지도 이때 0).
  const [seen] = useState(readSeen);
  useEffect(() => { markManualSeen(); }, []);

  const parts = useMemo(() => {
    const q = term.trim().toLowerCase();
    return MANUAL.map((p) => ({
      ...p,
      sections: p.sections.filter((s) =>
        (all || !s.screen || canScreen(me, s.screen, 'read')) &&
        (!q || [s.title, s.menu, ...s.steps, s.note?.text ?? ''].some((t) => t.toLowerCase().includes(q)))),
    })).filter((p) => p.sections.length > 0);
  }, [me, all, term]);
  const hidden = MANUAL.reduce((n, p) => n + p.sections.length, 0) - parts.reduce((n, p) => n + p.sections.length, 0);

  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="space-y-3">
      <div className="page-head">
        <h1 className="font-bold">사용 설명서</h1>
        <p className="text-sm text-ink-soft">
          화면의 빨간 번호 순서대로 따라 하시면 됩니다. 시스템이 바뀌면 이 설명서도 같이 바뀝니다.
          {Number.isFinite(BUILD) && <> · 최근 업데이트 <b>{kst(BUILD)}</b></>}
        </p>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <input className="field w-64" placeholder="찾을 말 (예: 할부, 붙여넣기, 마감)" value={term} onChange={(e) => setTerm(e.target.value)} />
        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          전체 보기
          <span className="hidden text-xs text-ink-faint sm:inline">— 끄면 내가 쓰는 메뉴만</span>
        </label>
        <button className="btn-ghost ml-auto text-sm" onClick={() => go('changes')}>최근 바뀐 것</button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]">
        <nav className="hidden self-start lg:sticky lg:top-3 lg:block">
          <div className="card max-h-[calc(100vh-6rem)] overflow-y-auto p-2 text-sm">
            <button className="block w-full rounded px-2 py-1 text-left font-semibold hover:bg-canvas" onClick={() => go('changes')}>최근 바뀐 것</button>
            {parts.map((p) => (
              <div key={p.id} className="mt-2">
                <p className="px-2 py-1 text-xs font-bold text-ink-faint">{p.title}</p>
                {p.sections.map((s) => (
                  <button key={s.id} className="block w-full truncate rounded px-2 py-1 text-left hover:bg-canvas" onClick={() => go(s.id)}>
                    {s.title}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </nav>

        <div className="min-w-0 space-y-4">
          <Changes seen={seen} />
          {parts.map((p) => (
            <div key={p.id} className="space-y-3">
              <div className="pt-2">
                <h2 className="text-lg font-bold">{p.title}</h2>
                <p className="text-sm text-ink-soft">{p.desc}</p>
              </div>
              {p.sections.map((s) => <Section key={s.id} s={s} />)}
            </div>
          ))}
          {parts.length === 0 && <p className="card p-4 text-sm text-ink-soft">찾는 말이 들어간 장이 없습니다.</p>}
          {!all && !term && hidden > 0 && (
            <button className="btn-ghost text-sm" onClick={() => setAll(true)}>다른 메뉴 설명 {hidden}장 더 보기</button>
          )}
        </div>
      </div>
    </div>
  );
}
